import {
  AnniProjectSupportDataType,
  isNew,
} from '../AnniProjectPopupInterface';
import { anniversaryProjectVo } from '@/__generated__/FrontApi';
import IconMsg from '@/assets/img/icons/ico_letter_blue@2x.png';
import IconMsg2 from '@/assets/img/icons/ico_msg@2x.png';
import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import Col from '@/components/Grid/Col';
import Row from '@/components/Grid/Row';
import { InputCheckbox } from '@/components/Input/InputCheckbox';
import InputTextarea from '@/components/Input/InputTextarea';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { SelectItemDefaultData } from '@/helpers/GtmHelper';
import { SUPPORT_TERM_ONCE } from '@/helpers/SupportHelper';
import { navigate } from 'gatsby';
import moment from 'moment';
import React, { FC, useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { GtmButtonDonate } from '@/components/Button.gtm';
import Section from '@/components/Section';

const NoDataMessage = styled.div`
  margin: 30px 0;
  max-width: 768px;
  display: inline-block;
  text-align: initial;
  width: 100%;
  text-align: center;

  ${breakpoint(`mobile`)} {
    padding: 32px 0 40px;
  }
`;

const GiftForm = styled.form`
  ${Row} {
    margin-top: 5px;
  }
  ${ErrorMessage} {
    margin-bottom: 10px;
  }
  .font_sm {
    font-size: 14px;
    margin-top: 13px;

    ${breakpoint(`mobile`)} {
      font-size: 12px;
      margin-top: 8px;
    }
  }
`;

/* <Text> 안에 서로 다른 3개의 속성에 따른 list style값 */
const Text = styled.span<{
  listStyle?: boolean;
  titStyle?: boolean;
  titStyle2?: boolean;
}>`
  display: inline-block;
  position: relative;
  line-height: 1.7;
  ${({ listStyle }) => {
    if (listStyle) {
      return `
      &::before {
        content: '';
        display: inline-block;
        width: 5px;
        height: 5px;
        background: #1cabe2;
        border-radius: 100%;
        vertical-align: middle;
        margin: -4px 10px 0 4px;

        ${breakpoint(`mobile`)} {
          width: 4px;
          height: 4px;
        }
      }
      `;
    }
    return ``;
  }}
  ${({ titStyle }) => {
    if (titStyle) {
      return `
      &::before {
        content: '';
        display: inline-block;
        width: 18px;
        height: 13px;
        background: url(${IconMsg}) no-repeat;
        background-size: contain;
        vertical-align: middle;
        margin: -2px 10px 0 2px;
      }
        `;
    }
    return ``;
  }}
  ${({ titStyle2 }) => {
    if (titStyle2) {
      return `
      &::before {
        content: '';
        display: inline-block;
        width: 18px;
        height: 15px;
        background: url(${IconMsg2}) no-repeat;
        background-size: contain;
        vertical-align: middle;
        margin: -2px 10px 0 2px;
      }
        `;
    }
    return ``;
  }}
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
  margin: auto;

  ${Section} {
    width: 100%;
  }

  ${breakpoint(`mobile`)} {
    padding-left: 0;
    margin-top: 32px;
    button {
      width: 100%;
      margin: 0 auto;
    }
  }
`;

const AnniGiftForm: FC<{ onRequestClose: any; datas: any }> = ({
  onRequestClose,
  datas,
}) => {
  const [items, setItems] = useState<anniversaryProjectVo>(datas);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AnniProjectSupportDataType>({});

  useEffect(() => {
    setItems(datas);
  }, [datas]);

  const onSubmitHandler = useCallback(
    (data: AnniProjectSupportDataType) => {
      const inputData = { ...data };
      if (data.openNameYn !== 'N') inputData.openNameYn = 'Y';
      if (data.openAmountYn !== 'N') inputData.openAmountYn = 'Y';
      inputData.anniDate = items?.anniDate
        ? moment(items.anniDate).format('YYYY.MM.DD')
        : '';

      navigate(`/donate`, {
        state: {
          anniProjectData: inputData,
        },
      });

      // 창을 닫아야 후원 페이지에서 scroll이 생성됨
      onRequestClose();
    },
    [items, onRequestClose],
  );
  if (datas === null || items?.displayStateTypeCode !== '3')
    return (
      <NoDataMessage className="no-data">
        {items?.displayStateTypeName || '종료'} 된 프로젝트입니다.
      </NoDataMessage>
    );

  return (
    <GiftForm onSubmit={handleSubmit(onSubmitHandler)}>
      <input
        type="hidden"
        name="aprjtIdx"
        ref={register}
        value={items?.aprjtIdx || ''}
      />
      <input type="hidden" name="supportTerm" ref={register} value="FM02" />
      <input type="hidden" name="supportCategory" ref={register} value="D224" />
      <input
        type="hidden"
        name="atype"
        ref={register}
        value={items?.anniType?.replaceAll('AT', '') || ''}
      />
      <Text titStyle>
        <b>기념 메시지 남기기</b>
      </Text>
      <div className="textarea_bgbox">
        <InputTextarea
          placeholder="기념 메시지를 입력하고 후원하기 버튼을&#13;&#10;누르시면 메시지를 남기실 수 있습니다&#13;&#10;(300자 이내)"
          name="story"
          ref={register({
            required: {
              value: true,
              message: `기념 메시지를 입력해주세요.`,
            },
            maxLength: {
              value: 300,
              message: `300자 이내로 입력해주세요.`,
            },
          })}
        />
      </div>
      {/* 에러 메시지 */}
      {errors.story && <ErrorMessage>{errors.story.message}</ErrorMessage>}

      <FormGroup>
        <ul>
          <li>
            <Text listStyle>
              <b>
                욕설이나 타인을 비방하는 글을 작성시 운영자에 의해 삭제 될 수
                있습니다.
              </b>
            </Text>
          </li>
          <li>
            <Text listStyle>
              <b>후원내역을 비공개하시겠습니까?</b>
            </Text>
            <Row>
              <Col>
                <InputCheckbox
                  ref={register}
                  label="이름 비공개"
                  name="openNameYn"
                  value="N"
                />
              </Col>
              <Col>
                <InputCheckbox
                  ref={register}
                  label="금액 비공개"
                  name="openAmountYn"
                  value="N"
                />
              </Col>
            </Row>
          </li>
        </ul>
      </FormGroup>

      <ButtonContainer>
        <GtmButtonDonate
          type="submit"
          goto=""
          text="후원하기"
          color="yellow"
          full
          className="btn-support-together"
          gtmInfo={{
            ...SelectItemDefaultData,
            itemId: 'D224',
            itemName: '기념기부',
            itemCategory: SUPPORT_TERM_ONCE,
          }}
          onClickPreEvent={() => {
            // TODO onClick before call gtm
            // alert('click');
          }}
        />
      </ButtonContainer>
      {isNew ? (
        <>
          <p className="font_sm">
            * 이벤트 참여를 위해 해당 화면을 캡쳐해 주세요 (이벤트 기간 한정)
          </p>
        </>
      ) : (
        <></>
      )}
    </GiftForm>
  );
};

export default AnniGiftForm;
